import CommonForm from "Components/Common/CommonForm";
import React, { useEffect, useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { Button, Spinner, Modal, ModalHeader, ModalBody } from "reactstrap";
import { showToastMessage } from "common/service/CommonService";
import { userAddEditFormFields } from "../helpers/userconstant";
import { AddEditUserAssignmentProps, Assignment, FormValues } from "../helpers/user.types";

const AddEditUserAssignment: React.FC<
  AddEditUserAssignmentProps & {
    isOpen: boolean;
    toggle: () => void;
  }
> = ({
  isOpen,
  toggle,
  existingData,
  onSave,
  onCancel,
  loading = false,
  userAssignmentData = [],
  roleDropdownData = [],
  mode = "add",
}) => {
  const [assignments, setAssignments] = useState<Assignment[]>(
    existingData?.assignments || []
  );

  const isEditMode = Boolean(existingData?.assignments?.length);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    trigger,
    setValue,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    mode: "onSubmit",
    defaultValues: {
      username: "",
      roleid: null,
    },
  });

  useEffect(() => {
    if (isEditMode && existingData?.assignments?.[0]) {
      reset({
        username: existingData.assignments[0].username,
        roleid: existingData.assignments[0].roleid,
      });
    }
  }, [isEditMode, existingData, reset]);

  const isDuplicateRole = (username: string, roleid: number): boolean => {
    return (
      assignments.some((a) => a.username === username && a.roleid === roleid) ||
      userAssignmentData.some(
        (s) => s.username === username && s.roleid === roleid
      )
    );
  };

  const onAddMore: SubmitHandler<FormValues> = (values) => {
    const { username, roleid } = values; // ← values from handleSubmit are already correct
    if (!username || roleid === null) return;

    if (isDuplicateRole(username, roleid)) {
      showToastMessage("error", "This role is already assigned to this user.");
      return;
    }

    const role = roleDropdownData.find((r) => r.value === roleid);

    setAssignments((prev) => [
      ...prev,
      {
        userid: 0,
        username,
        roleid,
        rolename: role?.label,
      },
    ]);

    reset({ username: "", roleid: null });
  };

  const onFinalSave = async () => {
    if (assignments.length === 0) {
      const isValid = await trigger();
      if (!isValid) return;
    }

    const { username, roleid } = watch();
    let finalAssignments = [...assignments];

    if (username && roleid !== null) {
      const exists = finalAssignments.some(
        (a) => a.username === username && a.roleid === roleid
      );

      if (!exists && !isDuplicateRole(username, roleid)) {
        const role = roleDropdownData.find((r) => r.value === roleid);

        finalAssignments.push({
          userid: 0,
          username,
          roleid,
          rolename: role?.label,
        });
      }
    }

    if (!finalAssignments.length) return;

    onSave({ assignments: finalAssignments });
  };

const onFinalEdit = async () => {
  const isValid = await trigger();
  if (!isValid) return;

  const values = getValues();          // ← use getValues not watch()
  const { username, roleid } = values;
  if (!username || roleid === null) return;

  const recordId = existingData?.assignments?.[0]?.userassignmentid;
  const role = roleDropdownData.find((r) => r.value === roleid);

  onSave({
    assignments: [
      {
        userassignmentid: recordId,
        userid: existingData?.assignments?.[0]?.userid || 0,
        username,
        roleid,
        rolename: role?.label,
      },
    ],
  });

};

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size="md"
      backdrop="static"
      centered
      className="common_modal"
    >
      <ModalHeader toggle={toggle} className="border-bottom p-3">
        {mode === "assign"
          ? "Assign Role To User"
          : isEditMode
          ? "Edit User Assignment"
          : "Add User Assignment"}
      </ModalHeader>

      <ModalBody>
        <form onSubmit={(e) => e.preventDefault()}>
          {assignments.length > 0 && !isEditMode && (
            <div className="mb-3">
              <h6>Added Assignments:</h6>
              <ul className="list-group">
                {assignments.map((a, i) => (
                  <li key={i} className="list-group-item">
                    {a.username} - {a.rolename}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <CommonForm
            columns={1}
            control={control}
            errors={errors as any}
            fields={userAddEditFormFields(isEditMode, roleDropdownData)}
              setValue={setValue}      // ← pass this
             getValues={getValues}   // ← pass this
          />

          <div className="d-flex justify-content-end gap-2 mt-4">
            <Button color="dark" outline onClick={toggle}>
              Cancel
            </Button>

         

            <Button
              color="primary"
              outline
              disabled={loading || isSubmitting}
              onClick={isEditMode ? onFinalEdit : onFinalSave}
            >
              {(loading || isSubmitting) && (
                <Spinner size="sm" className="me-2" />
              )}
              Save
            </Button>
          </div>
        </form>
      </ModalBody>
    </Modal>
  );
};

export default AddEditUserAssignment;